import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import { save as sendNotification } from 'store/notification/notification'
import * as snackbar from 'utils/snackbar'
import { canNotify, studentName } from './List'

const TEMPLATE_CODE = 'missing-contact-reminder'

const CHANNEL_LABELS = { email: 'Email', }
const CHANNEL_NAMES = { email: ['email', 'emails'] }

const toRecord = row => ({
  _id: row.personId,
  parentName: [row.parentFirstName, row.parentLastName].filter(Boolean).join(' '),
  studentName: studentName(row),
  ...(row.parentEmail && { recipientEmail: row.parentEmail }),
  ...(row.parentPhone && { recipientPhone: row.parentPhone }),
})

export default function NotifyDialog({ target, rows, onClose }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [notifyAll, setNotifyAll] = useState(false)
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState(null)

  const pending = rows.filter(canNotify)
  const recipients = notifyAll ? pending : target && canNotify(target) ? [target] : []

  const nameFor = id => {
    const row = rows.find(item => String(item.personId) === String(id))
    return row ? studentName(row) : `#${id}`
  }

  const close = () => {
    if (!sending) onClose()
  }

  const handleSend = async () => {
    setSending(true)
    const response = await dispatch(sendNotification({ templateCode: TEMPLATE_CODE, payload: recipients.map(toRecord) }))
    setSending(false)

    if (response?.error) {
      snackbar.error(response.error?.data?.error || t('Could not send the notification.'))
      return
    }

    const outcome = { sent: 0, byChannel: {}, failed: [], skipped: [] }
    for (const [channel, group] of Object.entries(response.payload?.channels ?? {})) {
      const failed = group.notifications ?? []
      const skipped = group.skipped ?? []
      outcome.byChannel[channel] = { sent: group.sentCount ?? 0, failed: failed.length, skipped: skipped.length }
      outcome.sent += group.sentCount ?? 0
      outcome.failed.push(...failed.map(item => ({ ...item, channel })))
      outcome.skipped.push(...skipped.map(item => ({ ...item, channel })))
    }

    if (outcome.sent) {
      const parts = Object.entries(outcome.byChannel)
        .filter(([, tally]) => tally.sent > 0)
        .map(([channel, tally]) => `${tally.sent} ${t(CHANNEL_NAMES[channel]?.[tally.sent === 1 ? 0 : 1] ?? 'notifications')}`)
      snackbar.success(`${parts.join(' · ')} ${t('sent.')}`)
    }

    if (outcome.failed.length || outcome.skipped.length) setResult(outcome)
    else onClose()
  }

  return (
    <Dialog open={Boolean(target)} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{result ? t('Notification Results') : t('Send Notification')}</DialogTitle>
      <DialogContent dividers>
        {result ? (
          <Stack spacing={2}>
            <Stack spacing={0.75}>
              {Object.entries(result.byChannel).map(([channel, tally]) => (
                <Stack key={channel} direction="row" alignItems="center" spacing={1}>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={tally.failed ? 'error' : 'success'}
                    label={t(CHANNEL_LABELS[channel] ?? channel)}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {`${tally.sent} ${t('sent')} · ${tally.failed} ${t('failed')} · ${tally.skipped} ${t('skipped')}`}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            {result.skipped.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="warning.main">{`${t('Skipped')} (${result.skipped.length})`}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('No reachable contact on file for these students.')}
                </Typography>
                <Stack sx={{ mt: 0.5 }}>
                  {result.skipped.map((item, index) => (
                    <Typography key={`${item.id}-${item.channel}-${index}`} variant="body2">
                      <Typography component="span" variant="caption" color="text.secondary">
                        {`${t(CHANNEL_LABELS[item.channel] ?? item.channel)} · `}
                      </Typography>
                      {nameFor(item.id)}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}

            {result.failed.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="error.main">{`${t('Failed')} (${result.failed.length})`}</Typography>
                <Stack sx={{ mt: 0.5 }}>
                  {result.failed.map((item, index) => (
                    <Typography key={`${item.id ?? 'failed'}-${item.channel}-${index}`} variant="body2">
                      <Typography component="span" variant="caption" color="text.secondary">
                        {`${t(CHANNEL_LABELS[item.channel] ?? item.channel)} · `}
                      </Typography>
                      {item.subjectName || t('Unknown recipient')}
                      {(item.reason || item.status) && (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {` — ${item.reason || item.status}`}
                        </Typography>
                      )}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        ) : (
          <>
            {notifyAll ? (
              <Typography variant="body2" fontWeight={900}>
                {`${t('All parents/guardians of students without a contact will be notified')} (${pending.length}).`}
              </Typography>
            ) : (
              <Stack direction="row" spacing={1}>
                <Typography variant="body1">{t('Ask the parent or guardian of')}</Typography>
                <Typography variant="body1" fontWeight={900}>
                  {target ? studentName(target) : ''}
                </Typography>
                <Typography variant="body1">{t('to add a contact.')}</Typography>
              </Stack>
            )}

            <Divider sx={{ my: 2 }} />

            <FormControlLabel
              control={<Checkbox checked={notifyAll} disabled={sending} onChange={event => setNotifyAll(event.target.checked)} />}
              label={t('Notify all parents/guardians of students without a contact')}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button variant="contained" onClick={onClose}>
            {t('CLOSE')}
          </Button>
        ) : (
          <>
            <Button onClick={close} disabled={sending}>
              {t('CANCEL')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={sending || !recipients.length}
              startIcon={sending ? <CircularProgress size={20} /> : null}
            >
              {sending ? t('SENDING...') : `${t('SEND')}${recipients.length > 1 ? ` (${recipients.length})` : ''}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
